<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import ClusterMap from '@/components/map/ClusterMap.vue';
import Swal from 'sweetalert2';
import { RepositoryFactory } from '@/data/repositoryFactory.js';
import { handleResponseWithMessages } from '@/helpers/wwwHelper.js';
import { useMapLogic } from '../../composables/mapLogic';
import {
  getDemPointsForMapMarkers,
  getActionsWithNoDemPointForMapMarkers,
  getWorkersStartStopMarkers
} from '../../helpers/actions/actionMarkersHelper.js';
import {
  REMOVE_ACTION_BUTTON_TYPE_NAME,
  HIDE_ACTION_BUTTON_TYPE_NAME,
  COPY_TO_CLIPBOARD_BUTTON_TYPE_NAME,
  SHOW_ACTION_GALLERY_BUTTON_TYPE_NAME,
  UPDATE_ACTION_BUTTON_TYPE_NAME,
  ACTIVATE_DEACTIVATE_DEMPOINT_BUTTON_TYPE_NAME,
  SHOW_COORDS_ON_MAP_BUTTON_TYPE_NAME,
  PREVIOUS_ACTION_BUTTON_TYPE_NAME,
  NEXT_ACTION_BUTTON_TYPE_NAME,
  ADD_ACTION_TO_DEMPOINT_BUTTON_TYPE_NAME,
  ADD_EDIT_DEMPOINT_NOTE_BUTTON_TYPE_NAME,
  ENSURE_ACTION_ADDITIONAL_MARKERS_VISIBLE_BUTTON_TYPE_NAME,
  SHOW_DEM_POINT_DUPLICATES_BUTTON_TYPE_NAME,
  UPDATE_UWAGI_FZ_BUTTON_TYPE_NAME
} from '../../data/constants/buttonsNamesConstants.js';
import { copyToClipboard, waitUntil } from '../../helpers/utilsHelper';
import OrderSummary from '@/components/orders/OrderSummary.vue';
import ActionsTable from '../actions/ActionsTable.vue';
import { isNotEmptyArray } from '../../helpers/utilsHelper';
import { getSwalConfiguration } from '../../helpers/swalHelper';
import ManualOrderPerform from '../actions/ManualOrderPerform.vue';
import ActionEditModalView from '../actions/ActionEditModalView.vue';
import { createToaster } from '@meforma/vue-toaster';
import { useAuthStore } from '../../stores/auth';
import { ACCESS_ACTION_ADD_BUTTON } from '../../data/constants/authConstants';
import { addGroupsCountInfoToActions } from '../../helpers/actions/actionMarkersHelper.js';
import { getAvailableActionTypes } from '../../helpers/actions/actionsHelper.js';
import { showOnGoogleMaps } from '../../helpers/navigator';
import {
  save as saveMapSnapshotHelper,
  tryApply as tryApplyMapSnapshotHelper
} from '../../helpers/map/mapSnapshotHelper.js';
import OrderActionsMapSelectionSummary from './OrderActionsMapSelectionSummary.vue';
import {
  ACTION_FROM_DEM_POINT_MARKER_TYPE_ID,
  ACTION_PHOTO_MARKER_TYPE_ID,
  DEM_POINT_MARKER_TYPE_ID,
  SINGLE_DEM_POINT_TYPE_ID
} from '../../data/constants/appConstants.js';
import { getCreatedActionMarkerTooltip } from '../../helpers/tooltips/actionMarkersTooltipsHelper.js';
import { useActionsAdditionalMarkers } from '../../composables/actions/actionsAdditionalMarkers.js';
import ModalGallery from '../utils/ModalGallery.vue';
import { v4 as uuidv4 } from 'uuid';
import { addEditDemPointsNote } from '../../helpers/demPointsHelper.js';
import { AVAILABLE_MODES, useModesStore } from '../../stores/storeModes.js';
import { ZEN_MODE_HEADER_HEIGHT } from '../../data/constants/appConstants.js';
import DemPointDuplicatesPopup from '../demPoints/DemPointDuplicatesPopup.vue';
import { useModifyTextInSwal } from '../../composables/modifyTextInSwal.js';
import repositoryGeneric from '../../data/repositoryGeneric.js';
import {
  TOASTER_ERROR_OCCURED_MSG,
  TOASTER_SERVER_ERROR_MSG
} from '../../data/constants/toasterMessagesConstants.js';

const MAP_SNAPSHOT_KEY = 'HdjsxSOxmXDqHqXxfgdZ';
const HIDE_OPERATIONAL_BUTTONS_IN_MARKERS_TOOLTIPS = false;
const RepositoryOrders = RepositoryFactory.get('orders');
const RepositoryActions = RepositoryFactory.get('actions');
const RepositoryDemPoints = RepositoryFactory.get('demPoints');

const props = defineProps({ orderId: Number, orderNumber: String, order: Object });
const emit = defineEmits(['refreshAll']);
const router = useRouter();
const route = useRoute();
const isLoading = ref(false);
const demPointsForMap = ref(null);
const actionsWithNoDemPointForMap = ref(null);
const workersStartStopData = ref(null);
const actionsTable = ref();
const alreadyLoadedTabs = ref({ orderSummary: true, actionsTable: false });
const disabledActionTypesIds = ref([]);
const disabledFailureReasonsIds = ref([]);
const disabledAdditionalDismantlingSourcesIds = ref([]);
const disabledUserOrdersIds = ref([]);
const enabledUserOrdersStartStopsIds = ref([]);
const showOnlyLastActionsFilterEnabled = ref(false);
const showInactiveDemPointsFilter = ref(true);
const afterVerificationFilter = ref(null);
const manualOrderPerform = ref(null);
const actionEditModalView = ref();
const toaster = createToaster({ position: 'top-right' });
const authStore = useAuthStore();
const {
  clusterMap,
  selectedMarkers,
  onSelectedMarkersChanged,
  onSingleMarkerSelected,
  resetSelectedIfCtrl,
  updateMarker,
  updateTooltip,
  focusOnMarker
} = useMapLogic();
const ordersSummaryRef = ref();
const dataInitialized = ref(false);
const filtersInitialized = ref(false);
const actionsAdditionalMarkers = useActionsAdditionalMarkers(clusterMap);
const modalGallery = ref();
const modalGalleryId = uuidv4();
const modalGalleryPath = ref(null);
const modesStore = useModesStore();
const demPointDuplicatesPopup = ref();
const modifyTextInSwal = useModifyTextInSwal();

var markersTooltipsToRollBack = [];

const idActionForSearch = computed(() => {
  return parseInt(route.params.actionId);
});

const idDemPointForSearch = computed(() => {
  return parseInt(route.query.demPointId);
});

const availableActionTypes = computed(() => {
  return getAvailableActionTypes(demPointsForMap.value, actionsWithNoDemPointForMap.value);
});

// Important: this constains demPoints that have actions assigned to them as well
const selectedSingleDemPointsIds = computed(() => {
  if (!isNotEmptyArray(selectedMarkers.value)) return [];

  return selectedMarkers.value
    .filter(
      (marker) =>
        (marker.data.markerType === DEM_POINT_MARKER_TYPE_ID &&
          marker.data.demPointTypeId === SINGLE_DEM_POINT_TYPE_ID) ||
        marker.data.markerType === ACTION_FROM_DEM_POINT_MARKER_TYPE_ID
    )
    .map((marker) =>
      marker.data.markerType === DEM_POINT_MARKER_TYPE_ID ? marker.markerId : marker.data.demPointId
    );
});

const isZenMode = computed(() => modesStore.isActive(AVAILABLE_MODES.ZEN));

watch(
  () => props.orderId,
  () => {
    initialize();
  }
);

watch(
  () => isZenMode.value,
  () => {
    onZenModeChanged();
  }
);

async function initialize() {
  await fetchMarkersInfo(true);
}

async function fetchMarkersInfo(setBoundsAfterRefresh = false, callbackAfterMapRefresh = () => {}) {
  isLoading.value = true;

  demPointsForMap.value = await RepositoryOrders.getDemPointsForMap(props.orderId);
  actionsWithNoDemPointForMap.value = await RepositoryOrders.getActionsWithNoDemPointForMap(
    props.orderId
  );
  addGroupsCountInfoToActions(demPointsForMap.value, actionsWithNoDemPointForMap.value);

  workersStartStopData.value = await RepositoryOrders.getWorkersStartStopData(props.orderId);
  dataInitialized.value = true;

  await waitUntil(() => filtersInitialized.value);

  prepareMarkersForMap(setBoundsAfterRefresh, callbackAfterMapRefresh);

  isLoading.value = false;
}

function prepareMarkersForMap(setBoundsAfterRefresh = false, callbackAfterMapRefresh = () => {}) {
  const demPointsForMapMarkers = getDemPointsForMapMarkers(
    demPointsForMap.value,
    disabledActionTypesIds.value,
    disabledFailureReasonsIds.value,
    disabledAdditionalDismantlingSourcesIds.value,
    disabledUserOrdersIds.value,
    showOnlyLastActionsFilterEnabled.value,
    showInactiveDemPointsFilter.value,
    true,
    false,
    '',
    true,
    authStore,
    HIDE_OPERATIONAL_BUTTONS_IN_MARKERS_TOOLTIPS,
    {
      selectableActionsOverride: true,
      selectableDemPointsOverride: true,
      showActionsBrowser: true,
      afterVerificationFilter: afterVerificationFilter.value
    }
  );

  const actionsWithNoDemPointForMapMarkers = getActionsWithNoDemPointForMapMarkers(
    actionsWithNoDemPointForMap.value,
    disabledActionTypesIds.value,
    disabledFailureReasonsIds.value,
    disabledAdditionalDismantlingSourcesIds.value,
    disabledUserOrdersIds.value,
    true,
    '',
    authStore,
    HIDE_OPERATIONAL_BUTTONS_IN_MARKERS_TOOLTIPS,
    { selectableActionsOverride: true }
  );

  const workersStartStopMarkers = getWorkersStartStopMarkers(
    workersStartStopData.value,
    enabledUserOrdersStartStopsIds.value
  );

  const markers = demPointsForMapMarkers.concat(
    actionsWithNoDemPointForMapMarkers,
    workersStartStopMarkers
  );

  clusterMap.value?.initializeMap({
    markersData: markers,
    additionalAction: () => {
      onMapReady();
      callbackAfterMapRefresh();
    },
    setBoundsAfterRefresh
  });
}

function refreshMapBounds() {
  clusterMap.value.refreshMapAfterTick();
}

function onMapClickableClicked(clickable) {
  let actionId = clickable.getAttribute('id_action');
  let clickActionType = clickable.getAttribute('click_action_type');

  if (clickActionType == undefined) return;

  const lat = clickable.getAttribute('lat');
  const lon = clickable.getAttribute('lon');
  const tag = clickable.getAttribute('tag');
  const xid = clickable.getAttribute('xid');
  const actionTypeName = clickable.getAttribute('action_type_name');
  const demPointId = clickable.getAttribute('id_demPoint');
  const demPointAddActionAllowed = clickable.getAttribute('demPoint_add_action_allowed');
  let actionMarkerId = clickable.getAttribute('id_action_marker');

  switch (clickActionType) {
    case REMOVE_ACTION_BUTTON_TYPE_NAME:
      if (actionId != undefined) {
        onDeleteActionButtonClicked(actionId);
      }
      break;
    case HIDE_ACTION_BUTTON_TYPE_NAME:
      if (actionId != undefined) {
        onHideActionButtonClicked(actionId);
      }
      break;
    case COPY_TO_CLIPBOARD_BUTTON_TYPE_NAME:
      if (lat != undefined && lon != undefined) {
        copyToClipboard(lat + ',' + lon);
      }
      if (tag != undefined) {
        copyToClipboard(tag);
      }
      if (xid != undefined) {
        copyToClipboard(xid);
      }
      break;
    case SHOW_COORDS_ON_MAP_BUTTON_TYPE_NAME:
      if (lat != undefined && lon != undefined) {
        showOnGoogleMaps(lat, lon);
      }
      break;
    case SHOW_ACTION_GALLERY_BUTTON_TYPE_NAME:
      if (actionId != undefined && actionTypeName != undefined) {
        onShowActionGalleryButtonClicked(actionId, actionTypeName);
      }
      break;
    case UPDATE_ACTION_BUTTON_TYPE_NAME:
      if (actionId != undefined) {
        onActionUpdateButtonClicked(parseInt(actionId));
      }
      break;
    case ACTIVATE_DEACTIVATE_DEMPOINT_BUTTON_TYPE_NAME:
      if (demPointId != undefined && demPointAddActionAllowed != undefined) {
        onDemPointActivateDeactivateButtonClicked(demPointId, demPointAddActionAllowed);
      }
      break;
    case PREVIOUS_ACTION_BUTTON_TYPE_NAME:
      onShowAnotherDemPointActionButtonClicked(actionId, demPointId, -1);
      break;
    case NEXT_ACTION_BUTTON_TYPE_NAME:
      onShowAnotherDemPointActionButtonClicked(actionId, demPointId, 1);
      break;
    case ADD_ACTION_TO_DEMPOINT_BUTTON_TYPE_NAME:
      if (typeof demPointId === 'string' && !isNaN(demPointId)) {
        onAddActionButtonClicked(parseInt(demPointId));
      } else {
        console.warn('onMapClickableClicked(): Incorrect value of demPointId');
      }
      break;
    case ADD_EDIT_DEMPOINT_NOTE_BUTTON_TYPE_NAME:
      if (typeof demPointId === 'string' && !isNaN(demPointId)) {
        onAddEditDemPointNoteButtonClicked(parseInt(demPointId));
      } else {
        console.warn('onMapClickableClicked(): Incorrect value of demPointId');
      }
      break;
    case ENSURE_ACTION_ADDITIONAL_MARKERS_VISIBLE_BUTTON_TYPE_NAME:
      actionsAdditionalMarkers.ensureActionMarkersAdditionalMarkersVisible(actionMarkerId);
      break;
    case SHOW_DEM_POINT_DUPLICATES_BUTTON_TYPE_NAME:
      if (typeof demPointId === 'string' && !isNaN(demPointId)) {
        onShowDemPointDuplicatesButtonClicked(parseInt(demPointId));
      } else {
        console.warn('onMapClickableClicked(): Incorrect value of demPointId');
      }
      break;
    case UPDATE_UWAGI_FZ_BUTTON_TYPE_NAME:
      if (actionId != undefined) {
        onUpdateUwagiFZButtonClicked(actionId, demPointId);
      }
      break;
  }
}

/*
  actionIdInDemPointDiff - e.g. if it's equal to 1, it means that we want to show the next action in demPoint
*/
function onShowAnotherDemPointActionButtonClicked(actionId, demPointId, actionIdInDemPointDiff) {
  const { demPoint, markerRef } = findDemPointAndItsMarker(demPointId);

  if (!demPoint) {
    console.warn('onShowPreviousActionButtonClicked(): DemPoint not found.');
    return;
  }

  if (!markerRef) {
    console.warn('onShowPreviousActionButtonClicked(): MarkerRef not found.');
    return;
  }

  if (!isNotEmptyArray(demPoint.actions)) return;
  const currentAction = demPoint.actions.find((action) => action.id == actionId);

  const indexInDemPointOfWantedAction =
    demPoint.actions.indexOf(currentAction) + actionIdInDemPointDiff;

  if (indexInDemPointOfWantedAction < 0 || indexInDemPointOfWantedAction >= demPoint.actions.length)
    return;

  const wantedAction = demPoint.actions[indexInDemPointOfWantedAction];
  const originalPopupCaption =
    tryToGetOriginalMarkerTooltip(markerRef) ?? markerRef.data.popupCaption;

  markerRef.data.popupCaption = getCreatedActionMarkerTooltip(
    demPoint.lat,
    demPoint.lon,
    wantedAction,
    demPoint,
    authStore,
    HIDE_OPERATIONAL_BUTTONS_IN_MARKERS_TOOLTIPS,
    true
  );
  updateTooltip(markerRef);
  markerRef.openPopup();

  markersTooltipsToRollBack.push({
    markerRef,
    originalPopupCaption
  });
}

function findDemPointAndItsMarker(demPointId) {
  const demPoint = demPointsForMap.value?.find((demPoint) => demPoint.id == demPointId);
  const markerRef = clusterMap.value.markersRef.find(
    (markerRef) => markerRef.data.demPointId != null && markerRef.data.demPointId == demPointId
  );

  return { demPoint, markerRef };
}

function tryToGetOriginalMarkerTooltip(markerRef) {
  if (!isNotEmptyArray(markersTooltipsToRollBack)) return null;

  const foundMarkerTooltipInfo = markersTooltipsToRollBack.find((m) => m.markerRef === markerRef);

  return foundMarkerTooltipInfo?.originalPopupCaption;
}

function rollBackMarkersTooltips() {
  if (!isNotEmptyArray(markersTooltipsToRollBack)) return;

  markersTooltipsToRollBack.forEach((m) => {
    m.markerRef.data.popupCaption = m.originalPopupCaption;
    updateTooltip(m.markerRef);
  });

  markersTooltipsToRollBack = [];
}

function onDeleteActionButtonClicked(actionId) {
  Swal.fire(
    getSwalConfiguration('Usuwanie akcji', 'Czy na pewno chcesz usunąć wybraną akcję?')
  ).then((result) => {
    if (result.value) deleteAction(actionId);
  });
}

async function deleteAction(actionId) {
  var response = await RepositoryActions.deleteAction(actionId);
  handleResponseWithMessages(response, 'Akcja została usunięta.', () => {
    emit('refreshAll');
  });
}

function onHideActionButtonClicked(actionId) {
  Swal.fire(
    getSwalConfiguration('Ukrywanie akcji', 'Czy na pewno chcesz ukryć wybraną akcję?')
  ).then((result) => {
    if (result.value) toggleActionVisibility(actionId, false);
  });
}

async function toggleActionVisibility(actionId, visible) {
  var response = await RepositoryActions.toggleActionVisibility(actionId, visible);

  handleResponseWithMessages(response, 'Akcja została ukryta.', () => {
    onActionVisibilityChanged();
  });
}

function onShowActionGalleryButtonClicked(actionId, actionTypeName) {
  let routeData = router.resolve({
    name: 'singleActionGallery',
    query: {
      orderId: props.orderId,
      actionId: actionId,
      actionTypeName: actionTypeName,
      orderNumber: props.orderNumber
    }
  });

  window.open(routeData.href, '_blank');
}

function onDemPointActivateDeactivateButtonClicked(demPointId, demPointAddActionAllowed) {
  const header = demPointAddActionAllowed == 1 ? 'Dezaktywacja' : 'Aktywacja';
  const message = `Czy na pewno chcesz ${
    demPointAddActionAllowed == 1 ? 'dezaktywować' : 'aktywować'
  } punkt?`;

  Swal.fire(getSwalConfiguration(header, message)).then((result) => {
    if (result.value) {
      updateDemPointActivationState(demPointId, demPointAddActionAllowed == 1 ? 0 : 1);
    }
  });
}

async function updateDemPointActivationState(demPointId, newValue) {
  var response =
    newValue == 1
      ? await RepositoryDemPoints.activateDemPoint(demPointId)
      : await RepositoryDemPoints.deactivateDemPoint(demPointId);

  handleResponseWithMessages(
    response,
    newValue ? 'Aktywowano punkt.' : 'Dezaktywowano punkt.',
    () => {
      fetchMarkersInfo();
    }
  );
}

function onAddEditDemPointNoteButtonClicked(demPointId) {
  const demPoint = demPointsForMap.value?.find((demPoint) => demPoint.id === demPointId);

  if (!demPoint) {
    console.warn(
      'onAddEditDemPointNoteButtonClicked(): Something went wrong, demPoint with given id not found.'
    );
    return;
  }

  addEditDemPointsNote(props.orderId, [demPoint], fetchMarkersInfo);
}

function onShowDemPointDuplicatesButtonClicked(demPointId) {
  const demPoint = demPointsForMap.value?.find((demPoint) => demPoint.id === demPointId);

  if (!demPoint) {
    console.warn(
      'onShowDemPointDuplicatesButtonClicked(): Something went wrong, demPoint with given id not found.'
    );
    return;
  }

  demPointDuplicatesPopup.value.show(demPoint.id, demPoint.oznaczenie);
}

function onUpdateUwagiFZButtonClicked(actionId, demPointId) {
  let uwagiFZ = '';

  if (demPointId != null) {
    const demPoint = demPointsForMap.value?.find((demPoint) => demPoint.id == demPointId);

    if (!demPoint) {
      console.warn('onUpdateUwagiFZButtonClicked(): DemPoint not found.');
      return;
    }

    const action = demPoint.actions.find((action) => action.id == actionId);
    if (!action) {
      console.warn('onUpdateUwagiFZButtonClicked(): Action not found in DemPoint.');
      return;
    }

    uwagiFZ = action.uwagi_fz;
  } else {
    const action = actionsWithNoDemPointForMap.value.find((action) => action.id == actionId);
    if (!action) {
      console.warn('onUpdateUwagiFZButtonClicked(): Action not found in actionsWithNoDemPoint.');
      return;
    }

    uwagiFZ = action.uwagi_fz;
  }

  modifyTextInSwal.show('Aktualizuj uwagi FZ', uwagiFZ, updateUwagiFZ, actionId);
}

async function updateUwagiFZ(newValue, actionId) {
  isLoading.value = true;

  const response = await repositoryGeneric.fetchPostAndQueryParams(
    `action/${actionId}/setUwagiFz`,
    null,
    {
      uwagi_fz: newValue
    }
  );

  if (response?.serverError) {
    toaster.show(TOASTER_SERVER_ERROR_MSG, { type: 'error' });
  } else {
    if (response?.data?.id != null) {
      toaster.show('Uwagi FZ zostały zaktualizowane.', { type: 'success' });
      fetchMarkersInfo(false, () => {
        const marker = clusterMap.value?.markersRef.find(
          (m) => m.data.actionId === parseInt(actionId)
        );
        if (marker) {
          focusOnMarker(marker);
        }
      });
    } else {
      toaster.show(TOASTER_ERROR_OCCURED_MSG, { type: 'error' });
    }
  }

  isLoading.value = false;
}

function onTabClicked(tabId) {
  switch (tabId) {
    case 0:
      alreadyLoadedTabs.value.orderSummary = true;
      break;
    case 1:
      alreadyLoadedTabs.value.actionsTable = true;
      break;
  }
}

function onFiltersChanged(
  updatedDisabledActionTypesIds,
  updatedDisabledFailureReasonsIds,
  updatedDisabledAdditionalDismantlingSourcesIds,
  updatedDisabledUserOrdersIds,
  updatedEnabledUserOrdersStartStopsIds,
  updatedShowOnlyLastActionsFilter,
  updatedShowInactiveDemPointsFilter,
  updatedAfterVerificationFilter
) {
  disabledActionTypesIds.value = updatedDisabledActionTypesIds;
  disabledFailureReasonsIds.value = updatedDisabledFailureReasonsIds;
  disabledAdditionalDismantlingSourcesIds.value = updatedDisabledAdditionalDismantlingSourcesIds;
  disabledUserOrdersIds.value = updatedDisabledUserOrdersIds;
  enabledUserOrdersStartStopsIds.value = updatedEnabledUserOrdersStartStopsIds;
  showOnlyLastActionsFilterEnabled.value = updatedShowOnlyLastActionsFilter;
  showInactiveDemPointsFilter.value = updatedShowInactiveDemPointsFilter;
  afterVerificationFilter.value = updatedAfterVerificationFilter;

  if (filtersInitialized.value) {
    prepareMarkersForMap();
  } else {
    filtersInitialized.value = true;
  }
}

function onActionVisibilityChanged() {
  fetchMarkersInfo();
  actionsTable.value?.reloadCurrentPage();
}

function onAddActionButtonClicked(demPointId) {
  if (props.order?.last_worker) {
    manualOrderPerform.value.show(demPointId);
  } else {
    toaster.show('Aby utworzyć akcję, proszę najpierw dodać wykonawcę.', {
      duration: 3000,
      type: 'warning'
    });
  }
}

function onActionUpdateButtonClicked(actionId) {
  actionEditModalView.value.show(actionId);
}

function onMarkerClickedOverride(markerToSelect) {
  if (
    markerToSelect.data.markerType === ACTION_PHOTO_MARKER_TYPE_ID &&
    markerToSelect.data.resourcePath
  ) {
    showPhotoInModal(markerToSelect.data.resourcePath);
    return;
  }

  actionsAdditionalMarkers.hideAll();
  onSingleMarkerSelected(markerToSelect);
  const groupId = markerToSelect.data.groupId;
  updateMarkersTransparencyBasedOnGroupId(groupId);

  if (markerToSelect?.data?.actionId != null) {
    actionsAdditionalMarkers.tryToShowAdditionalMarkers(markerToSelect);
  }
}

function showPhotoInModal(resourcePath) {
  modalGalleryPath.value = import.meta.env.VITE_VUE_APP_IMAGES_URL + resourcePath;
  modalGallery.value.currentSourceIndex = 0;
  modalGallery.value.show();
}

function onMapMouseUp() {
  if (clusterMap.value?.isBeingDragged === false) {
    resetSelectedIfCtrl();
    updateMarkersTransparencyBasedOnGroupId(null);
    actionsAdditionalMarkers.hideAll();
  }
}

function onMapMarkersPopupClosed() {
  rollBackMarkersTooltips();
}

function updateMarkersTransparencyBasedOnGroupId(groupId) {
  const transparencyClassName = 'transparency-30';

  clusterMap.value.markersRef.forEach((markerRef) => {
    const classNameSource = markerRef.data.divIcon ?? markerRef.data;

    if (groupId == null && classNameSource.className === transparencyClassName) {
      classNameSource.className = '';
      updateMarker(markerRef);
    }

    if (
      groupId != null &&
      groupId === markerRef.data.groupId &&
      classNameSource.className === transparencyClassName
    ) {
      classNameSource.className = '';
      updateMarker(markerRef);
    } else if (
      groupId != null &&
      groupId !== markerRef.data.groupId &&
      classNameSource.className !== transparencyClassName
    ) {
      classNameSource.className = transparencyClassName;
      updateMarker(markerRef);
    }
  });
}

function onMapReady() {
  if (!isNaN(parseInt(idActionForSearch.value))) {
    focusMapOnActionWithId(idActionForSearch.value);
  } else if (!isNaN(parseInt(idDemPointForSearch.value))) {
    focusMapOnDemPointWithId(idDemPointForSearch.value);
  }

  tryApplyMapSnapshot();
}

function focusMapOnActionWithId(actionId) {
  const foundMarker = clusterMap.value.markersRef.find(
    (marker) => marker.data.actionId === actionId
  );

  if (foundMarker) {
    focusOnMarker(foundMarker);
    return;
  }

  // if marker not found, it may be an older action on a demPoint
  for (const demPoint of demPointsForMap.value) {
    if (isNotEmptyArray(demPoint.actions)) {
      const wantedAction = demPoint.actions.find((action) => action.id === actionId);
      if (wantedAction) {
        const { markerRef } = findDemPointAndItsMarker(demPoint.id);
        if (markerRef) {
          const originalPopupCaption =
            tryToGetOriginalMarkerTooltip(markerRef) ?? markerRef.data.popupCaption;

          markerRef.data.popupCaption = getCreatedActionMarkerTooltip(
            demPoint.lat,
            demPoint.lon,
            wantedAction,
            demPoint,
            authStore,
            HIDE_OPERATIONAL_BUTTONS_IN_MARKERS_TOOLTIPS,
            true
          );
          updateTooltip(markerRef);
          focusOnMarker(markerRef);
          markerRef.openPopup();

          markersTooltipsToRollBack.push({
            markerRef,
            originalPopupCaption
          });
        }
        break;
      }
    }
  }
}

function focusMapOnDemPointWithId(demPointId) {
  const foundMarker = clusterMap.value.markersRef.find(
    (marker) =>
      (marker.data.markerType === DEM_POINT_MARKER_TYPE_ID &&
        marker.data.markerId === demPointId) ||
      (marker.data.markerType === ACTION_FROM_DEM_POINT_MARKER_TYPE_ID &&
        marker.data.demPointId === demPointId)
  );

  if (foundMarker) {
    focusOnMarker(foundMarker);
  }
}

function onActionUpdateSucceeded() {
  ordersSummaryRef.value?.saveSummaryFiltersSnapshot();
  saveMapSnapshot();
  emit('refreshAll');
}

function saveMapSnapshot() {
  saveMapSnapshotHelper(clusterMap, MAP_SNAPSHOT_KEY);
}

function tryApplyMapSnapshot() {
  tryApplyMapSnapshotHelper(clusterMap, MAP_SNAPSHOT_KEY);
}

async function onZenModeChanged() {
  await nextTick();

  clusterMap.value?.invalidateSize();
}

onMounted(() => {
  if (props.orderId != null) {
    initialize();
  }
});

defineExpose({ refreshMapBounds });
</script>

<template>
  <div>
    <div class="row">
      <div class="sticky-container" :class="`col-lg-${isZenMode ? 12 : 8}`">
        <div class="ibox">
          <div class="ibox-content" :class="{ 'sk-loading': isLoading }">
            <div class="sk-spinner sk-spinner-three-bounce">
              <div class="sk-bounce1"></div>
              <div class="sk-bounce2"></div>
              <div class="sk-bounce3"></div>
            </div>

            <ClusterMap
              ref="clusterMap"
              :height="isZenMode ? `calc(100vh - ${ZEN_MODE_HEADER_HEIGHT})` : '700px'"
              :showZeroPositions="false"
              :disableClusteringAtZoomOverride="19"
              :maxClusterRadiusOverride="1"
              :refreshable="true"
              :areaSelecting="true"
              @selected-markers-changed="onSelectedMarkersChanged"
              @mapMouseUp="onMapMouseUp"
              @markerClicked="onMarkerClickedOverride"
              @clickableClicked="onMapClickableClicked"
              @popupClosed="onMapMarkersPopupClosed"
            >
              <button
                v-if="
                  isNotEmptyArray(selectedSingleDemPointsIds) &&
                  authStore.canAccess(ACCESS_ACTION_ADD_BUTTON)
                "
                class="btn btn-primary"
                style="position: absolute; top: 10px; right: 55px; z-index: 500"
                @click="() => onAddActionButtonClicked()"
              >
                Dodaj akcję
              </button>

              <button
                class="btn btn-primary"
                style="position: absolute; top: 10px; right: 10px; z-index: 500; padding: 6px 10px"
                @click="modesStore.toggleMode(AVAILABLE_MODES.ZEN)"
                title="Tryb pełnoekranowy (Ctrl + Shift + Z)"
              >
                <i :class="isZenMode ? 'fa fa-compress' : 'fa fa-arrows-alt'" />
              </button>

              <button
                class="btn btn-primary"
                style="position: absolute; bottom: 10px; left: 10px; z-index: 500"
                @click="refreshMapBounds"
              >
                <i class="fa fa-refresh" />
              </button>
            </ClusterMap>
          </div>
        </div>
      </div>

      <div v-show="!isZenMode" class="col-lg-4">
        <div class="tabs-container">
          <ul class="nav nav-tabs" role="tablist">
            <li @click="onTabClicked(0)">
              <a class="nav-link active" data-toggle="tab" href="#order-actions-details-tab-1">
                <i class="fa fa-info" />
                Informacje
              </a>
            </li>
            <li id="actionsTab" @click="onTabClicked(1)">
              <a class="nav-link" data-toggle="tab" href="#order-actions-details-tab-2">
                <img
                  src="/static/img/owner-gray.png"
                  alt="Typy akcji"
                  width="13"
                  height="13"
                  style="margin-right: 6px"
                />
                Akcje
              </a>
            </li>
          </ul>
          <div class="tab-content">
            <div role="tabpanel" id="order-actions-details-tab-1" class="tab-pane active">
              <div class="panel-body">
                <OrderActionsMapSelectionSummary
                  class="pl-3"
                  :orderId="props.orderId"
                  :selectedMarkers="selectedMarkers"
                  :demPoints="demPointsForMap"
                  @pointsDeactivated="fetchMarkersInfo"
                  @pointsNotesModified="fetchMarkersInfo"
                />

                <OrderSummary
                  ref="ordersSummaryRef"
                  v-if="alreadyLoadedTabs.orderSummary"
                  :orderId="props.orderId"
                  :order="props.order"
                  :actionTypes="availableActionTypes"
                  :parentInitialized="dataInitialized"
                  @refresh="emit('refreshAll')"
                  @filtersChanged="onFiltersChanged"
                />
              </div>
            </div>

            <div role="tabpanel" id="order-actions-details-tab-2" class="tab-pane">
              <div class="panel-body">
                <button
                  v-if="authStore.canAccess(ACCESS_ACTION_ADD_BUTTON)"
                  class="btn btn-outline-primary btn-xs mb-3"
                  @click="() => onAddActionButtonClicked()"
                >
                  Dodaj akcję
                </button>

                <ActionsTable
                  v-if="alreadyLoadedTabs.actionsTable"
                  ref="actionsTable"
                  :orderId="props.orderId"
                  :longerSearchBar="true"
                  @actionVisibilityChanged="onActionVisibilityChanged"
                  @actionUpdateButtonClicked="onActionUpdateButtonClicked"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ManualOrderPerform
      ref="manualOrderPerform"
      :orderId="props.orderId"
      :demPointsForMap="demPointsForMap"
      :actionsWithNoDemPointForMap="actionsWithNoDemPointForMap"
      :selectedDemPointsIds="selectedSingleDemPointsIds"
      @succeeded="emit('refreshAll')"
    />

    <ActionEditModalView
      ref="actionEditModalView"
      :orderId="props.orderId"
      :demPointsForMap="demPointsForMap"
      :actionsWithNoDemPointForMap="actionsWithNoDemPointForMap"
      @succeeded="onActionUpdateSucceeded"
    />

    <ModalGallery ref="modalGallery" :sources="[modalGalleryPath]" :id="modalGalleryId" />

    <DemPointDuplicatesPopup ref="demPointDuplicatesPopup" />
  </div>
</template>

<style scoped>
.sticky-container {
  position: sticky;
  top: 0;
  align-self: flex-start;
  /* height: 100vh; probably not necessary, but adds scrollbar in case of "short" browser window*/
  overflow-y: auto;
}
</style>
